import { ChangeEvent } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModelT } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { AnyServiceModel } from "@/Patient/Models/Appointment/AnyServiceModel";
import { IPatientAppointmentModel } from "@/Patient/Models/UI/Appointment/IPatientAppointmentModel";

interface IServiceTabSearch {
    Model: IPatientAppointmentModel;
}

interface IServiceTabSearchState {
    activeTab: string;
}

class ServiceTabSearch extends React.Component<IServiceTabSearch, IServiceTabSearchState> {
    public constructor(props: IServiceTabSearch) {
        super(props);
    }

    private getServiceForEmployeeRender(
        inputType: "radio" | "checkbox",
        siteIndex: number,
        index: number,
        onClickEvent: () => void,
        label: JSX.Element,
        keySuffix: string,
        checked: boolean,
        enableCustomDurationEdit: boolean,
        defaultDurationLabel: JSX.Element = undefined
    ): JSX.Element {
        const showCustomDurationEdit = enableCustomDurationEdit && checked;

        return (
            <div className={inputType} key={`${siteIndex}${index}${keySuffix}`}>
                <label
                    onClick={() => onClickEvent()}
                    className="service-tab-patient-label"
                >
                    <input
                        key={`${siteIndex}${index}${keySuffix}Radio`}
                        type={inputType}
                        checked={checked}
                    />
                    <div className="flex-container">
                        {label}
                        <div className="text-right" style={{ whiteSpace: "nowrap" }}>
                            {!showCustomDurationEdit &&
                                defaultDurationLabel
                            }
                            {showCustomDurationEdit && (
                                <TextboxControl
                                    key={`${siteIndex}${index}${keySuffix}TextBox`}
                                    onMountFocus
                                    IsNumeric
                                    value={this.props.Model.get("CustomDuration").toString()}
                                    className="form-control"
                                    onChange={(value: number) =>
                                        this.onDurationChange(Number(value))
                                    }
                                    style={{ width: "60px", height: "20px" }}
                                />
                            )}
                        </div>
                    </div>
                </label>
            </div>
        );
    }

    private getClinicianRadioButtonRender(clinician: EmployeeUIModel, onClickEvent: (clinician: EmployeeUIModel) => void): JSX.Element {
        return (
            <div className="radio" key={clinician.ContactFacetId} id={clinician.ContactFacetId}>
                <label title={clinician.Name}>
                    <input
                        type="radio"
                        radioGroup="radio_clinicians"
                        onChange={() => onClickEvent(clinician)}
                        key={clinician.ContactFacetId}
                        id={clinician.ContactFacetId}
                        checked={clinician.Checked}
                    />
                    {clinician.Name}
                </label>
            </div>
        );
    }

    private getClinics(site: SitesModel, siteServices: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[], anyServiceList: AnyServiceModel[], index: number, anyPossibleMatchingTabs: boolean): JSX.Element[] {
        return cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch
            ? this.getClinics_MultiServiceSearch(site, siteServices, index, anyPossibleMatchingTabs)
            : this.getClinics_SingleServiceSearch(site, siteServices, anyServiceList, index, anyPossibleMatchingTabs);
    }

    private getClinics_SingleServiceSearch(site: SitesModel, siteServices: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[], anyServiceList: AnyServiceModel[], index: number, anyPossibleMatchingTabs: boolean): JSX.Element[] {
        const activeTab = this.props.Model.get("ActiveTab");

        const siteIsCurrentTab = site.Model.Id === activeTab || !anyPossibleMatchingTabs && index === 0;

        const renderServiceTabContent = (): JSX.Element[] => anyServiceList
            .map((service, anyIdx) => this.getServiceForEmployeeRender(
                "radio",
                index,
                anyIdx,
                () => this.onAnyForSelectedSiteClick(service),
                <label>
                    <strong>
                        {cacheStore.GetString("AnyLabel")}
                    </strong>
                </label>,
                "__n",
                service.isSelected && !service.anyExceptEmergencies,
                true
            ))
            .concat(anyServiceList.map((service, anyIdx) => this.getServiceForEmployeeRender(
                "radio",
                index,
                anyIdx,
                () => this.onAnyForSelectedSiteClick(service),
                <label>
                    <strong>
                        {cacheStore.GetString("AnyExceptUrgentLabel")}
                    </strong>
                </label>,
                "__n1",
                service.isSelected && service.anyExceptEmergencies,
                true
            )))
            .concat(siteServices.sort((service1, service2) => service1.Name.trim().localeCompare(service2.Name.trim())).map((service, subIdx) => this.getServiceForEmployeeRender(
                "radio",
                index,
                subIdx,
                () => this.onServiceForEmployeeClick(service),
                <div>
                    <label>
                        <strong>
                            {service.Name}
                        </strong>
                        {` (${service.Model.Speciality})`}
                        {service.Model.ComboId != null ? (" (Combo)") : ""}
                    </label>
                </div>,
                `${site.Model.Id}__n`,
                service.Checked,
                true,
                (!(service.Checked && service.Model.ComboId == null) && (
                    <label>
                        {service.Model.Duration} min.
                    </label>
                ))
            )));

        return [
            <div
                key={`siteServiceTabEmployee${index}`}
                className={`service-tab-patient-site-tab ${siteIsCurrentTab ? "active-site-tab" : "inactive-site-tab"} `}
                onClick={() => this.onClickSelectTab(site.Model.Id)}
            >
                {site.Model.ClinicalName}
            </div>,
            (siteIsCurrentTab && (
                <div
                    key={"siteServiceContentEmployee" + index}
                    className="service-tab-patient-site-tab-content"
                >
                    {renderServiceTabContent()}
                </div>
            ))
        ];
    }

    private getClinics_MultiServiceSearch(site: SitesModel, siteServices: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[], index: number, anyPossibleMatchingTabs: boolean): JSX.Element[] {
        const checkedCount = siteServices.filter(p => p.Checked).length;

        const allServicesSelected = checkedCount > 0
            && checkedCount === siteServices.length;

        const allNonUrgentSelected = checkedCount > 0
            && siteServices.filter(p => p.Checked === !p.Model.IsUrgent).length === siteServices.length;

        const activeTab = this.props.Model.get("ActiveTab");

        const siteIsCurrentTab = site.Model.Id === activeTab || !anyPossibleMatchingTabs && index === 0;

        const sortedServices = siteServices.sortByProperty(p => p.Name.trim());

        return [
            <div
                key={`siteServiceTabEmployee${index}`}
                className={`service-tab-patient-site-tab ${siteIsCurrentTab ? "active-site-tab" : "inactive-site-tab"} `}
                onClick={() => this.onClickSelectTab(site.Model.Id)}
            >
                {site.Model.ClinicalName}
            </div>,
            (siteIsCurrentTab && (
                <div
                    key={"siteServiceContentEmployee" + index}
                    className="service-tab-patient-site-tab-content"
                >
                    {this.getServiceForEmployeeRender(
                        "checkbox",
                        index,
                        0,
                        () => this.onMultiSearchAnyClick(site.Model.Id, false, !allServicesSelected),
                        <label>
                            <strong>
                                {cacheStore.GetString("AnyLabel")}
                            </strong>
                        </label>,
                        "__n",
                        allServicesSelected,
                        false
                    )}
                    {this.getServiceForEmployeeRender(
                        "checkbox",
                        index,
                        1,
                        () => this.onMultiSearchAnyClick(site.Model.Id, true, !allNonUrgentSelected),
                        <label>
                            <strong>
                                {cacheStore.GetString("AnyExceptUrgentLabel")}
                            </strong>
                        </label>,
                        "__n1",
                        allNonUrgentSelected,
                        false
                    )}
                    {sortedServices.map((service, subIdx) => this.getServiceForEmployeeRender(
                        "checkbox",
                        index + 2,
                        subIdx,
                        () => this.onServiceForEmployeeClick(service),
                        <div>
                            <label>
                                <strong>
                                    {service.Name}
                                </strong>
                                {` (${service.Model.Speciality})`}
                                {service.Model.ComboId ? (" (Combo)") : ""}
                                {service.Model.IsUrgent ? (` (${cacheStore.GetString("UrgentLabel")})`) : ""}
                            </label>
                        </div>,
                        `${site.Model.Id}__n`,
                        service.Checked,
                        checkedCount === 1 && !service.Model.ComboId,
                        (<label>{service.Model.Duration} min.</label>)
                    ))}
                </div>
            ))
        ];
    }

    public render(): JSX.Element {
        const isCliniciansTab = this.props.Model.get("IsClinicianFilterTab");

        let serviceForEmployeeRender: JSX.Element[] = null;

        if (isCliniciansTab) {
            const serviceList = this.props.Model.get("EmployeeServiceOrComboList");

            const serviceListAsArray = serviceList && serviceList.toArray();

            const anyServiceList = this.props.Model.get("AnyServiceList").toArray();
            let currentServiceList: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>[] = [];

            let currentActiveTabMatchesAvailableSites = false;

            const activeTabFromModel = this.props.Model.get("ActiveTab");
            let indexOfUserActiveSiteInServicesList = -1;

            serviceForEmployeeRender = (serviceListAsArray && serviceListAsArray.length > 0)
                &&
                cacheStore.SiteCache
                    .filter(site => {
                        const currentServices = serviceListAsArray.filter(m => m.Model.SiteId === site.Model.Id);

                        const currentSiteHasServices = currentServices.length > 0;

                        if (currentSiteHasServices) {
                            if (!currentActiveTabMatchesAvailableSites) {
                                currentActiveTabMatchesAvailableSites = site.Model.Id === activeTabFromModel;
                            }
                            currentServiceList = currentServiceList.concat(currentServices);
                        }
                        return currentSiteHasServices;
                    })
                    .map(
                        (site, idx) => {
                            if (site.Model.Id === sessionStore.getCurrentEmployee().getActiveSite().getId()) {
                                indexOfUserActiveSiteInServicesList = idx;
                            }

                            return this.getClinics(
                                site,
                                currentServiceList.filter(m => m.Model.SiteId === site.Model.Id),
                                anyServiceList.filter(b => b.siteId === site.Model.Id),
                                idx,
                                currentActiveTabMatchesAvailableSites
                            );
                        }
                    )
                    .map(
                        (site, idx, array) => {
                            if (indexOfUserActiveSiteInServicesList > -1) {
                                if (idx === 0) return array[indexOfUserActiveSiteInServicesList];
                                if (idx === indexOfUserActiveSiteInServicesList) return array[0];
                            }
                            return site;
                        }
                    )
                    .reduce((e1, e2) => e1.concat(e2))
                || [<div key="serviceForEmployeeRenderNoResults" />];
        }

        let serviceForServiceRender: JSX.Element[] = null;

        if (!isCliniciansTab) {
            const serviceList2 = this.props.Model.get("GlobalServiceOrComboFilteredList")
                ? this.props.Model.get("GlobalServiceOrComboFilteredList").toArray()
                : [];

            let indexOfUserActiveSiteInServicesList = -1;

            if (serviceList2 && serviceList2.length > 0) {
                serviceForServiceRender = cacheStore.SiteCache
                    .map((site, idx) => {
                        if (site.Model.Id === sessionStore.getCurrentEmployee().getActiveSite().getId()) {
                            indexOfUserActiveSiteInServicesList = idx;
                        }

                        var siteServices = serviceList2
                            .filter(service => service.Model.SiteId === site.Model.Id);

                        if (siteServices.length == 0) {
                            return (<div key={"sl_" + idx} />);
                        }
                        else {
                            const inputType = cacheStore.ClinicSettings.EnablePatientChartMultiServiceAvailabilitySearch
                                ? "checkbox"
                                : "radio";

                            return (
                                <div
                                    key={"sl_" + idx}
                                    className="service-tab-patient-tab-service-tab"
                                >
                                    <h4>{site.Model.ClinicalName}</h4>
                                    <div className="service-tab-patient-tab-service-tab content">
                                        {siteServices.sortByProperty(service => service.Name).map((service, subidx) => (
                                            <div className={inputType} key={"ni_" + idx + "_" + subidx}>
                                                <label onClick={() => this.onServiceForServiceClick(service)} className="service-tab-patient-label">
                                                    <input type={inputType} checked={service.Checked} />
                                                    <div className="flex-container">
                                                        <label>
                                                            <strong>
                                                                {`${service.Name} `}
                                                            </strong>

                                                            ({service.Model.Speciality}) {service.Model.ComboId != null
                                                                ? (" (Combo)")
                                                                : ""
                                                            }
                                                        </label>
                                                        <div className="text-right">
                                                            <label>{service.Model.Duration} min.</label>
                                                        </div>
                                                    </div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        }
                    })
                    .map(
                        (site, idx, array) => {
                            if (indexOfUserActiveSiteInServicesList > -1) {
                                if (idx === 0) return array[indexOfUserActiveSiteInServicesList];
                                if (idx === indexOfUserActiveSiteInServicesList) return array[0];
                            }
                            return site;
                        }
                    );
            }
        }

        return (
            <div key="searchFieldContainer" className="app-dual-tab-container" onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}>
                <div key="searchField" className="search-field" style={{ width: "255px", marginLeft: "20px" }}>
                    <div key="searchFieldImage" className="gf">
                        <span className="glyphicon glyphicon-search" />
                    </div>
                    <input type="text" autoFocus={true} onChange={this.onSearchTextChange.bind(this)} />
                </div>
                <div className="service-tab-patient-tab-result-container">
                    {!isCliniciansTab && (
                        <div className="service-tab-patient-search-by-services-tab">
                            {serviceForServiceRender}
                        </div>
                    )}
                    <div key="searchFieldResults" className="clinicians-search-field-result">
                        {(isCliniciansTab
                            || !isCliniciansTab
                            && this.props.Model.get("ServicesClinicianList")
                            && this.props.Model.get("ServicesClinicianList").count() > 0)
                            && (
                                <div
                                    key="serviceClinitians"
                                    className={`service-tab-patient-clinitian-list ${isCliniciansTab ? "clinicians-tab" : ""} `}
                                >
                                    {!isCliniciansTab && (
                                        <div>
                                            <CheckboxControl
                                                id="all_check"
                                                label={cacheStore.GetString("AllLabInbox")}
                                                operation={LocalPatientOperations.Appointment.OnClick_ServiceTabAllEmployee}
                                                checked={this.props.Model.get("IsAllEmployeeSelected")}
                                                target={CheckboxTarget.Myle_Patient_Appointment_OnClickAllService}
                                            />
                                            <CheckedListControl
                                                DataSource={this.props.Model.get("ServicesClinicianList")}
                                                Operation={LocalPatientOperations.Appointment.OnClick_ServicesClinician}
                                                sort={false}
                                                limitTextWidth={true}
                                            />
                                        </div>
                                    )}
                                    {isCliniciansTab && (this.props.Model.get("CliniciansFiltered").filter(c => c.Visible === true).map(item => (
                                        this.getClinicianRadioButtonRender(item, () => this.onClinicianForClinicianClick(item))
                                    )))}
                                </div>
                            )
                        }
                        {isCliniciansTab
                            && serviceForEmployeeRender
                            && (
                                <div
                                    key="serviceClinititestansContainer"
                                    className="serviceSearchByEmployeeContainer"
                                >
                                    <div
                                        className="service-tab-patient-tab-sites-nav-bar"
                                    >
                                        {serviceForEmployeeRender.filter((itm, i) => i % 2 === 0)}
                                    </div>
                                    <div
                                        className="service-tab-patient-tab-service-list"
                                    >
                                        {serviceForEmployeeRender.filter((itm, i) => i % 2 !== 0)}
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        );
    }

    public componentWillUnmount(): void {
        if (this.state && this.state.activeTab === "") {
            this.setState({ activeTab: cacheStore.SiteCache[0].Model.Id });
        }
    }

    private onClickSelectTab(tabId: string): void {
        const operation = new LocalEvent(LocalPatientOperations.Appointment.OnSearchTabSelected);
        operation.AddItem("activeTab", tabId);
        LocalEventDispatcher.dispatch(operation);
    }

    private onSearchTextChange(e: ChangeEvent<HTMLInputElement>): void {
        if (this.props.Model.get("IsClinicianFilterTab") === true) {
            const operation = new LocalEvent(LocalPatientOperations.Appointment.ClinicianSearchChanged);
            operation.AddItem("value", e.target.value);
            LocalEventDispatcher.dispatch(operation);
        }
        else {
            const operation = new LocalEvent(LocalPatientOperations.Appointment.ServiceSearchChanged);
            operation.AddItem("value", e.target.value);
            LocalEventDispatcher.dispatch(operation);
        }
    }

    private onServiceForEmployeeClick(item: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>): void {
        const operation = new LocalEvent(LocalPatientOperations.Appointment.OnServiceForEmployeeItemClick);
        operation.AddItem("value", item.Id);
        operation.AddItem("siteId", item.Model.SiteId);
        operation.AddItem("checked", !item.Checked);
        LocalEventDispatcher.dispatch(operation);
    }

    private onClinicianForClinicianClick(item: EmployeeUIModel): void {
        const operation = new LocalEvent(LocalPatientOperations.Appointment.OnClinicianTab_ClinicianSelected);
        operation.AddItem("id", item.Id);
        operation.AddItem("checked", !item.Checked);
        LocalEventDispatcher.dispatch(operation);
    }

    private onServiceForServiceClick(item: CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>): void {
        const operation = new LocalEvent(LocalPatientOperations.Appointment.OnClick_Services);
        operation.AddItem("checked", !item.Checked);
        operation.AddItem("id", item.Id);
        operation.AddItem("ComboId", item.Model.ComboId);
        operation.AddItem("serviceDefinitionId", item.Model.ServiceDefinitionId);
        LocalEventDispatcher.dispatch(operation);
    }

    private onAnyForSelectedSiteClick(item: AnyServiceModel): void {
        const operation = new LocalEvent(LocalPatientOperations.Appointment.on_ClinicianTab_AnyService_Click);
        operation.AddItem("siteId", item.siteId);
        operation.AddItem("exceptUrgent", item.anyExceptEmergencies);
        operation.AddItem("checked", !item.isSelected);
        LocalEventDispatcher.dispatch(operation);
    }

    private onMultiSearchAnyClick(siteId: string, exceptUrgent: boolean, checked: boolean): void {
        const operation = new LocalEvent(LocalPatientOperations.Appointment.on_ClinicianTab_AnyService_Click);
        operation.AddItem("siteId", siteId);
        operation.AddItem("exceptUrgent", exceptUrgent);
        operation.AddItem("checked", checked);
        LocalEventDispatcher.dispatch(operation);
    }

    private onDurationChange(duration: number): void {
        const operation = new LocalEvent(LocalPatientOperations.Appointment.OnServiceDurationEdit);
        operation.AddItem("duration", duration);
        LocalEventDispatcher.dispatch(operation);
    }
}

export { ServiceTabSearch };
